/**
 * Datadog observability bootstrap following the Itaú frontend standard:
 * RUM **and** Logs are both initialised, each with its own client token.
 * Mirrors the legacy Angular `AppComponent.addDatadog()` (RUM) and extends it
 * with the Logs browser SDK plus error/custom-log helpers.
 *
 * Initialisation is defensive: a misconfigured token must never break the page
 * bootstrap, so every SDK call is guarded. Configuration comes from `VITE_DD_*`
 * environment variables (see `.env.*`).
 */

const AGENT_BASE = 'https://www.datadoghq-browser-agent.com/us1/v6';

interface BrowserSdk {
  q?: Array<(...args: unknown[]) => void>;
  onReady(cb: () => void): void;
  init(config: Record<string, unknown>): void;
}

function loadAgent(globalName: 'DD_RUM' | 'DD_LOGS', file: string): void {
  const w = window as unknown as Record<string, BrowserSdk | undefined>;
  if (w[globalName]) return;

  const stub: BrowserSdk = {
    q: [],
    onReady(cb: () => void) {
      this.q?.push(cb);
    },
    init() {
      /* replaced once the real agent loads */
    },
  };
  w[globalName] = stub;

  const script = document.createElement('script');
  script.async = true;
  script.src = `${AGENT_BASE}/${file}`;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

function isTrue(value: string | undefined): boolean {
  return value === 'true';
}

export function initDatadog(): void {
  try {
    const env = import.meta.env.VITE_DD_ENV;
    const service = import.meta.env.VITE_DD_SERVICE;
    const version = import.meta.env.VITE_APP_VERSION || 'local';
    const sigla = import.meta.env.VITE_DD_SIGLA;
    const site = import.meta.env.VITE_DD_SITE || 'datadoghq.com';
    const useProxy = isTrue(import.meta.env.VITE_DD_USE_PROXY);
    const proxy = useProxy
      ? `https://proxy.datadog.${env}.aws.cloud.ihf:3844`
      : undefined;

    const rumAppId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
    const rumToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
    const logsToken = import.meta.env.VITE_DD_LOGS_CLIENT_TOKEN;
    const context = sigla ? { context: { sigla } } : {};

    if (rumAppId && rumToken) {
      loadAgent('DD_RUM', 'datadog-rum.js');
      window.DD_RUM?.onReady(() => {
        window.DD_RUM?.init({
          applicationId: rumAppId,
          clientToken: rumToken,
          site,
          service,
          env,
          version,
          ...(proxy ? { proxy } : {}),
          sessionSampleRate: 5,
          sessionReplaySampleRate: 5,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
          maskSensitiveData: true,
          ...context,
        });
      });
    }

    if (logsToken) {
      loadAgent('DD_LOGS', 'datadog-logs.js');
      window.DD_LOGS?.onReady(() => {
        window.DD_LOGS?.init({
          clientToken: logsToken,
          site,
          service,
          env,
          version,
          ...(proxy ? { proxy } : {}),
          forwardErrorsToLogs: true,
          forwardConsoleLogs: ['error'],
          sessionSampleRate: 5,
          maskSensitiveData: true,
          ...context,
        });
      });
    }
  } catch (error) {
    // Never let observability bootstrap break the application.
    console.error('Datadog initialization failed', error);
  }
}

/**
 * Reports an error to Datadog. Forwards to **RUM** (`addError`, the standard
 * error-tracking channel) and to **Logs** (`logger.error`) when available, and
 * always falls back to `console.error` so failures are never silently dropped.
 */
export function logError(message: string, context?: Record<string, unknown>): void {
  let delivered = false;

  if (window.DD_RUM) {
    window.DD_RUM.onReady(() => {
      window.DD_RUM?.addError(new Error(message), context);
    });
    delivered = true;
  }

  if (window.DD_LOGS) {
    window.DD_LOGS.onReady(() => {
      window.DD_LOGS?.logger.error(message, context);
    });
    delivered = true;
  }

  if (!delivered) {
    console.error(message, context);
  }
}

/**
 * Sends a custom informational log to Datadog Logs. No-op (debug console) when
 * the Logs agent is not initialised.
 */
export function logInfo(message: string, context?: Record<string, unknown>): void {
  if (window.DD_LOGS) {
    window.DD_LOGS.onReady(() => {
      window.DD_LOGS?.logger.info(message, context);
    });
    return;
  }
  console.debug(message, context);
}
